import React from 'react';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { MusicNote, Playlist } from '@phosphor-icons/react';

export default function ContentPicker({
  contentType,
  onContentTypeChange,

  songs = [],
  songId = '',
  onSongChange,

  playlists = [],
  playlistId = '',
  onPlaylistChange,

  onAddSong,
  onAddPlaylist,
  onRemoveSong,
  onRemovePlaylist,

  songTestId = 'content-song-select',
  playlistTestId = 'content-playlist-select',
}) {
  const isSong = contentType === 'song';

  return (
    <div className="space-y-3">
      <label className="font-mono text-[10px] uppercase text-zinc-500 tracking-wider">
        Content
      </label>

      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => onContentTypeChange('song')}
          className={`flex items-center gap-2 px-3 py-2 rounded-full border text-xs font-mono transition-colors ${
            isSong
              ? 'border-[#ccff00] bg-[#ccff00]/10 text-[#ccff00]'
              : 'border-zinc-800 text-zinc-400 hover:border-zinc-600'
          }`}
        >
          <MusicNote size={14} weight="fill" />
          Song
        </button>

        <button
          type="button"
          onClick={() => onContentTypeChange('playlist')}
          className={`flex items-center gap-2 px-3 py-2 rounded-full border text-xs font-mono transition-colors ${
            !isSong
              ? 'border-[#ccff00] bg-[#ccff00]/10 text-[#ccff00]'
              : 'border-zinc-800 text-zinc-400 hover:border-zinc-600'
          }`}
        >
          <Playlist size={14} weight="fill" />
          Playlist
        </button>
      </div>

      {isSong ? (
        <div className="flex flex-wrap gap-2">
          <Select value={songId} onValueChange={onSongChange}>
            <SelectTrigger
              data-testid={songTestId}
              className="bg-zinc-950 border-zinc-800 flex-1 min-w-[240px]"
            >
              <SelectValue
                placeholder={songs.length ? 'Pick a song' : 'No songs yet'}
              />
            </SelectTrigger>

            <SelectContent>
              {songs.map(song => (
                <SelectItem key={song.song_id} value={song.song_id}>
                  {song.title} — {song.artist}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {songId && onRemoveSong && (
            <Button
              type="button"
              variant="outline"
              onClick={() => onRemoveSong(songId)}
              className="border-red-500/40 text-red-400 hover:border-red-500/70 hover:text-red-300"
            >
              Remove
            </Button>
          )}

          {onAddSong && (
            <Button
              type="button"
              variant="outline"
              onClick={onAddSong}
              className="border-zinc-800 hover:border-zinc-600"
            >
              + Add song
            </Button>
          )}
        </div>
      ) : (
        <div className="flex flex-wrap gap-2">
          <Select value={playlistId} onValueChange={onPlaylistChange}>
            <SelectTrigger
              data-testid={playlistTestId}
              className="bg-zinc-950 border-zinc-800 flex-1 min-w-[240px]"
            >
              <SelectValue
                placeholder={
                  playlists.length ? 'Pick a playlist' : 'No playlists yet'
                }
              />
            </SelectTrigger>

            <SelectContent>
              {playlists.map(playlist => (
                <SelectItem
                  key={playlist.playlist_id}
                  value={playlist.playlist_id}
                >
                  {playlist.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {playlistId && onRemovePlaylist && (
            <Button
              type="button"
              variant="outline"
              onClick={() => onRemovePlaylist(playlistId)}
              className="border-red-500/40 text-red-400 hover:border-red-500/70 hover:text-red-300"
            >
              Remove
            </Button>
          )}

          {onAddPlaylist && (
            <Button
              type="button"
              variant="outline"
              onClick={onAddPlaylist}
              className="border-zinc-800 hover:border-zinc-600"
            >
              + Add playlist
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
